'use client';

import { IconExclamationCircle } from '@vs-shop/ui/icons';
import { Button } from '@vs-shop/ui/button';
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@vs-shop/ui/dialog';
import { Alert } from '@vs-shop/ui/alert';
import { showErrorToast, showSuccessToast } from '@vs-shop/ui/toast';
import Link from '@vs-shop/image-link/Link';
import { useIntl } from 'react-intl';

import { Url } from '../../../../route/router';
import type { ProductFavouriteListWithProductCodes } from '../../../../effect/schema/openapi/backend';
import ProductFavouritesModalCreateNewList from './ProductFavouritesModalCreateNewList';
import ProductFavouritesModalLists from './ProductFavouritesModalLists';
import { useFavouriteMembershipSave } from './useFavouriteMembershipSave';

type Props = {
  productCode: string;
  productName: string;
  listsData: readonly ProductFavouriteListWithProductCodes[];
  isLoading: boolean;
  onCloseAction: () => void;
  isFavourite: boolean;
};

export default function ProductFavouritesModalContent({
  productCode,
  productName,
  listsData,
  isLoading,
  onCloseAction,
  isFavourite,
}: Props) {
  const intl = useIntl();
  const { checkedListIds, validationError, isPending, handleCheckedChange, handleSave } =
    useFavouriteMembershipSave({
      productCode,
      listsData,
      isLoading,
      isFavourite,
      onSuccessNotification: () =>
        showSuccessToast(
          intl.formatMessage(
            {
              id: 'productFavouritesModal.save.success.toast',
              defaultMessage: 'Změny byly uloženy. {link}',
            },
            {
              link: (
                <Link href={Url.accountFavourites} key="link" className="underline">
                  {intl.formatMessage({
                    id: 'productFavouritesModal.save.success.toast.link',
                    defaultMessage: 'Jít na oblíbené produkty.',
                  })}
                </Link>
              ),
            }
          )
        ),
      onFailureNotification: () =>
        showErrorToast(
          intl.formatMessage({
            id: 'productFavouritesModal.save.error',
            defaultMessage: 'Nepodařilo se uložit změny v oblíbených produktech.',
          })
        ),
      onCloseAction,
    });

  return (
    <DialogContent data-slot="favourites-modal">
      {/* HEADER */}
      <DialogHeader>
        <DialogTitle>
          {intl.formatMessage({
            id: 'productFavouritesModal.heading',
            defaultMessage: 'Přidání produktu do oblíbených',
          })}
        </DialogTitle>
        <DialogDescription>{productName}</DialogDescription>
      </DialogHeader>

      {/* LISTS */}
      <ProductFavouritesModalLists
        lists={listsData}
        checkedListIds={checkedListIds}
        onCheckedChangeAction={handleCheckedChange}
        isLoading={isLoading}
      />

      {/* CREATE NEW */}
      <ProductFavouritesModalCreateNewList />

      {/* VALIDATION ERROR */}
      {validationError && (
        <Alert variant="warning" icon={IconExclamationCircle}>
          {intl.formatMessage({
            id: 'productFavouritesModal.validation.selectList',
            defaultMessage: 'Vyberte seznam, do kterého chcete přidat produkt.',
          })}
        </Alert>
      )}

      {/* BUTTONS */}
      <DialogFooter>
        <DialogClose
          render={
            <Button
              type="button"
              className="uppercase"
              size="sm"
              variant="secondary"
              disabled={isPending}
            >
              {intl.formatMessage({
                id: 'common.cancel',
                defaultMessage: 'Zrušit',
              })}
            </Button>
          }
        />

        <Button
          type="button"
          className="uppercase"
          size="sm"
          variant="primary"
          loading={isPending}
          disabled={isPending}
          onClick={handleSave}
        >
          {intl.formatMessage({
            id: 'productFavouritesModal.saveCahnges',
            defaultMessage: 'Uložit změny',
          })}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
